import { HandHeart, Info, Mail, Settings, Share2, ShieldCheck } from 'lucide-react'
import type { LanguagePack } from '../i18n/packs'
import { THEME_COPY } from '../i18n/modes/theme'
import type { ThemePref } from '../services/settings'
import type { Language } from '../types/document'
import { CONTACT_EMAIL, type Copy } from '../i18n/translations'

export interface AppSettings {
  helperMode: boolean
  listenMode: boolean
}

export function About({
  copy,
  p,
  settings,
  onSetting,
  onShareApp,
  language,
  theme,
  onTheme
}: {
  copy: Copy
  p: LanguagePack
  settings: AppSettings
  onSetting: (key: keyof AppSettings, on: boolean) => void
  onShareApp: () => void
  language: Language
  theme: ThemePref
  onTheme: (theme: ThemePref) => void
}) {
  const tc = THEME_COPY[language] ?? THEME_COPY.en
  return (
    <section className="about-view">
      <div className="page-title">
        <div>
          <p className="eyebrow">{copy.appName}</p>
          <h1>{copy.aboutTitle}</h1>
        </div>
      </div>

      <div className="about-block settings-block">
        <Settings size={26} aria-hidden="true" />
        <h2>{p.settingsTitle}</h2>
        <div role="group" aria-label={tc.appearance}>
          <strong className="block">{tc.appearance}</strong>
          <div className="theme-picker">
            {(['system', 'light', 'dark'] as const).map((t) => (
              <button key={t} aria-pressed={theme === t} onClick={() => onTheme(t)}>
                {tc[t]}
              </button>
            ))}
          </div>
        </div>
        <label className="setting-row">
          <input type="checkbox" checked={settings.listenMode} onChange={(e) => onSetting('listenMode', e.target.checked)} />
          <span>
            <strong>{p.listenMode}</strong>
            <small>{p.listenModeHint}</small>
          </span>
        </label>
        <label className="setting-row">
          <input type="checkbox" checked={settings.helperMode} onChange={(e) => onSetting('helperMode', e.target.checked)} />
          <span>
            <strong>{p.helperMode}</strong>
            <small>{p.helperModeHint}</small>
          </span>
        </label>
      </div>

      <div className="about-block">
        <ShieldCheck size={26} aria-hidden="true" />
        <ul className="privacy-list">
          {copy.privacyPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </div>
      <div className="about-block">
        <Info size={26} aria-hidden="true" />
        <h2>{copy.disclaimerTitle}</h2>
        <p>{copy.disclaimer}</p>
      </div>
      <div className="about-block">
        <Mail size={26} aria-hidden="true" />
        <h2>{copy.contactTitle}</h2>
        <p>
          {copy.contact}{' '}
          <a className="contact-link" href={`mailto:${CONTACT_EMAIL}?subject=Samjho`}>
            {CONTACT_EMAIL}
          </a>
        </p>
      </div>
      <div className="about-block">
        <HandHeart size={26} aria-hidden="true" />
        <h2>{copy.freeTitle}</h2>
        <p>{copy.free}</p>
      </div>

      <div className="about-share-box">
        <button className="whatsapp-share-btn" onClick={onShareApp}>
          <Share2 size={18} aria-hidden="true" />
          <span>{copy.shareApp}</span>
        </button>
      </div>
    </section>
  )
}
